import { Link } from "react-router-dom";
import { ArrowRight, Building2, Map, Truck, ShieldCheck, Monitor, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { useTranslation } from "../i18n/LanguageContext";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Home() {
  const t = useTranslation();

  const services = [
    { slug: "construction", title: t.svc_construction_title, description: t.svc_construction_desc, Icon: Building2 },
    { slug: "travaux-publics", title: t.svc_tp_title, description: t.svc_tp_desc, Icon: Map },
    { slug: "logistique", title: t.svc_logistique_title, description: t.svc_logistique_desc, Icon: Truck },
    { slug: "sst", title: t.svc_sst_title, description: t.svc_sst_desc, Icon: ShieldCheck },
    { slug: "digital", title: t.svc_digital_title, description: t.svc_digital_desc, Icon: Monitor },
  ];

  const figures = [
    { value: "5", label: t.fig_sectors },
    { value: "100%", label: t.fig_congolese },
    { value: "7", label: t.fig_trades },
    { value: "2", label: "Pays" },
  ];

  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center">
        <div className="max-w-content mx-auto px-6 md:px-12 w-full">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-3">
              <p className="text-[12px] uppercase tracking-[0.15em] font-semibold text-accent mb-8">
                Construction · Ingénierie · Technologie
              </p>
              <h1 className="text-display-sm md:text-display font-serif mb-6">
                Quality In Every Build
              </h1>
              <p className="text-secondary text-lg md:text-xl max-w-lg mb-10 leading-relaxed">
                {t.hero_description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.08em] font-semibold text-white bg-accent px-7 py-4 hover:bg-accent-dark transition-colors duration-300">
                  {t.hero_cta} <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.08em] font-semibold text-navy border border-navy px-7 py-4 hover:bg-navy hover:text-white transition-all duration-300">
                  {t.nav_cta}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2 hidden lg:block">
              <ImagePlaceholder
                label="RAVA"
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80&auto=format&fit=crop"
                aspectRatio="4/5"
              />
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
            <ChevronDown size={20} className="text-secondary/40" strokeWidth={1.5} />
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeIn} transition={{ duration: 0.4 }}>
            <p className="text-[12px] uppercase tracking-[0.15em] font-semibold text-accent mb-4">{t.home_services_label}</p>
            <h2 className="text-heading-sm md:text-heading font-serif mb-16">{t.home_services_title}</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeIn}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  className="group block border border-border p-10 md:p-12 hover:border-accent hover:-translate-y-1 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-400"
                >
                  <s.Icon size={32} className="text-accent mb-8" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                  <p className="text-secondary text-[15px] leading-relaxed mb-8">{s.description}</p>
                  <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-accent group-hover:gap-3 transition-all duration-300">
                    {t.home_learn_more} <ArrowRight size={14} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key figures */}
      <section className="py-24 md:py-32 bg-subtle">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {figures.map((f, i) => (
              <motion.div
                key={f.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`text-center ${i < 3 ? "md:border-r md:border-border" : ""}`}
              >
                <div className="font-serif text-6xl md:text-7xl text-primary mb-3">{f.value}</div>
                <div className="text-[13px] uppercase tracking-[0.1em] font-medium text-secondary">{f.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-navy py-20 md:py-24">
        <div className="max-w-content mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-heading-sm md:text-heading text-white mb-4">Un projet en tête ?</h2>
          <p className="text-footer-text text-lg mb-10 max-w-lg mx-auto">Contactez notre équipe pour discuter de vos besoins.</p>
          <Link to="/contact" className="inline-flex text-[13px] uppercase tracking-[0.08em] font-semibold text-navy bg-white px-8 py-4 hover:bg-accent hover:text-white transition-all duration-300">
            {t.nav_cta}
          </Link>
        </div>
      </section>
    </PageTransition>
  );
}
